import request from '@oskecare/fe-utilities/lib/request';
import { TROUBLE_TICKET } from '@oskecare/fe-utilities/lib/constants/dataSources';
import { cacheWithErrorCheck } from '../../helpers.js';

/**
 * @property {string} _customerProblemApiUrl
 * @property {Logger} _logger
 * @property {MessageBus} _messageBus
 * @property {Map<string, Promise<*>>} _cache
 */
export default class Claims {
  /**
   * @param {object} config
   * @param {Logger} logger
   * @param {MessageBus} messageBus
   */
  constructor({ config, logger, messageBus }) {
    this._customerProblemApiUrl = config.customerProblemApiUrl;
    this._logger = logger;
    this._messageBus = messageBus;
    this._cache = new Map();
  }

  /**
   * Proxy for `request` function with logSource pre-set
   *
   * @param {object} params
   * @return {Promise<*>}
   * @see Request.fetch
   * @private
   */
  request(params) {
    return request({
      ...params,
      logSource: 'infra-claims',
    }).catch(this._logger.catchError);
  }

  /**
   * @param {string} subscriptionId
   * @param {boolean} isNonessential
   * @return {Promise<*>}
   */
  getClaimsList(subscriptionId, isNonessential = false) {
    return cacheWithErrorCheck(this._cache, `claimsList-${subscriptionId}`, () =>
      this.request({
        method: 'GET',
        route: `${this._customerProblemApiUrl}/troubleTicket`,
        query: subscriptionId ? { subscriptionId } : null,
        dataSource: TROUBLE_TICKET,
        isNonessential,
      }).then(claims => claims.filter(claim => claim.visibleToCustomer)),
    );
  }

  /**
   * @param {string} claimId
   * @return {Promise<*>}
   */
  getClaimDetail(claimId) {
    return cacheWithErrorCheck(this._cache, `claim-${claimId}`, () =>
      this.request({
        method: 'GET',
        route: `${this._customerProblemApiUrl}/troubleTicket/${claimId}`,
        expectedErrorCodes: [403],
        dataSource: TROUBLE_TICKET,
      }),
    );
  }

  /**
   * Sets all unseen notifications of claim as seen.
   *
   * @param {string} claimId
   * @param {string[]} eventIds - ids of previously unread claim status changes
   * @param {boolean} isNonessential
   * @return {Promise<*>}
   */
  updateSeenByCustomer(claimId, eventIds, isNonessential = false) {
    const promises = eventIds.map(eventId =>
      this.request({
        method: 'PATCH',
        route: `${this._customerProblemApiUrl}/troubleTicket/${claimId}`,
        headers: {
          Accept: 'application/json; charset=utf-8',
          'Content-type': 'application/json-patch+json; charset=utf-8',
        },
        body: [
          {
            op: 'replace',
            path: `/statusChange[eventId=${eventId}]/seenByCustomer`,
            value: 'true',
          },
        ],
        dataSource: TROUBLE_TICKET,
        isNonessential,
      }),
    );

    return Promise.all(promises);
  }

  /**
   * Resets cache of specific claim detail
   *
   * @param {string} claimId
   */
  resetClaimDetailCache(claimId) {
    this._cache.delete(`claim-${claimId}`);
  }

  /**
   * Resets cache of the claims list of specific customer
   *
   * @param {string} subscriptionId
   */
  resetClaimsListCache(subscriptionId) {
    this._cache.delete(`claimsList-${subscriptionId}`);
  }
}
